import { useMemo } from 'react';
import { useStore } from '@nanostores/react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';
import { getApiCredentialsForEnvironmentQueryOptions } from '@/services/db/api-credential/queries';
import { getAudienceCredentialsQueryOptions } from '@/services/db/audience-credential/queries';
import { getAudiencesQueryOptions } from '@/services/db/audience/queries';
import {
  isApiForm,
  isConnectedOrDraftForm,
  isJotform,
  isScannedForm,
  isTypeform,
} from '@/services/db/custom-form/entity';
import { getCustomFormsQueryOptions } from '@/services/db/custom-form/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { isConnectedFacebookForm } from '@/services/db/facebook-lead-ads/entity';
import {
  getFacebookLeadAdsCredentialQueryOptions,
  getFacebookLeadAdsFormsQueryOptions,
} from '@/services/db/facebook-lead-ads/queries';
import { getIntegrationsQueryOptions } from '@/services/db/integration/queries';
import {
  getCdpOutEnabledQueryOptions,
  getDataOutEnabledQueryOptions,
  getMparticleIntegrationQueryOptions,
  getSegmentInPresentQueryOptions,
  getSegmentOutEnabledQueryOptions,
} from '@/services/db/integration/status-queries';
import { useIntegrationsWorkspaceId } from '../workspace-id';
import { useSqlImportsByDatabase } from '@/services/db/sql-import/queries';
import { warehouseSyncIsPaused, warehouseSyncType } from '@/services/db/warehouse-sync/entity';
import { getWarehouseSyncQueryOptions } from '@/services/db/warehouse-sync/queries';
import { getWebhooksQueryOptions } from '@/services/db/webhook/queries';
import { IntegrationType } from '../catalog/enums';
import { useProductTaxonomyTerms } from './taxonomy';
import {
  categoryOptions as computeCategoryOptions,
  directoryFilteredIntegrations,
  groupIntegrations,
  sortIntegrations,
  visibleIntegrationsDirectory,
} from './filter-sort-group';
import { buildIntegrationsList, type IntegrationsListConfig, type IntegrationsListSources } from './synthesize';
import type { IntegrationFilters } from './types';

/**
 * Fan out every data source the synthesized directory list consumes and
 * assemble the `IntegrationsListSources` bag. Each source degrades gracefully:
 * a failed status fetch resolves to its "not configured" default rather than
 * blanking the whole list.
 *
 * Overview-only credential sources (push / slack / twilio / in-app / URL-params)
 * are deferred to PR4 — `urlParamsEnabled` defaults to false here.
 */
// fallow-ignore-next-line complexity -- inherent fan-out: one TanStack query per data source (~15 independent sources) plus environment/taxonomy/account context. The hook count is the source count; splitting would only relocate it. Pure assembly logic lives in buildIntegrationsList.
export function useIntegrationsListSources(): {
  sources: IntegrationsListSources;
  config: IntegrationsListConfig;
  isLoading: boolean;
} {
  const workspaceId = useIntegrationsWorkspaceId();
  const qc = useQueryClient();
  const account = useStore(accountStore);
  const accountId = account?.id != null ? Number(account.id) : undefined;
  const terms = useProductTaxonomyTerms();
  const environment = useEnvironmentOptional(workspaceId);
  const enabledActions = environment.data?.environment?.enabled_actions ?? [];
  const twilioEnabled = enabledActions.includes('twilio');

  const catalog = useQuery(getIntegrationsQueryOptions(workspaceId, qc));
  const webhooks = useQuery(getWebhooksQueryOptions(workspaceId, qc));
  const audiences = useQuery(getAudiencesQueryOptions(workspaceId, qc));
  const audienceCredentials = useQuery(getAudienceCredentialsQueryOptions(workspaceId, qc));
  const warehouse = useQuery(getWarehouseSyncQueryOptions(workspaceId, qc));
  const sqlImports = useSqlImportsByDatabase(workspaceId);
  const customForms = useQuery(getCustomFormsQueryOptions(workspaceId, qc));
  const fbCredential = useQuery(getFacebookLeadAdsCredentialQueryOptions(workspaceId));
  const fbForms = useQuery(getFacebookLeadAdsFormsQueryOptions(workspaceId, qc));
  const mparticle = useQuery(getMparticleIntegrationQueryOptions(workspaceId));
  const cdpOut = useQuery(getCdpOutEnabledQueryOptions(workspaceId));
  const segmentIn = useQuery(getSegmentInPresentQueryOptions(workspaceId));
  const segmentOut = useQuery(getSegmentOutEnabledQueryOptions(workspaceId));
  const apiCredentials = useQuery({
    ...getApiCredentialsForEnvironmentQueryOptions(accountId ?? 0, workspaceId),
    enabled: accountId != null,
  });

  const backendIntegrations = catalog.data ?? [];
  const nativeDataOutIds = useMemo(
    () => backendIntegrations.filter((i) => i.type === IntegrationType.nativeDataOut).map((i) => i.id),
    [backendIntegrations],
  );
  const dataOutQueries = useQueries({
    queries: nativeDataOutIds.map((id) => getDataOutEnabledQueryOptions(workspaceId, id)),
  });
  const enabledDataOutIds = useMemo(() => {
    const set = new Set<string>();
    nativeDataOutIds.forEach((id, index) => {
      if (dataOutQueries[index]?.data === true) {
        set.add(id);
      }
    });
    return set;
  }, [nativeDataOutIds, dataOutQueries]);

  const facebookConnectedForms = (fbForms.data ?? []).filter(isConnectedFacebookForm).length;
  const connectedOrDraftForms = (customForms.data ?? []).filter(isConnectedOrDraftForm);
  const forms = {
    facebookLeadAds: fbCredential.data != null,
    customForms: connectedOrDraftForms.some((f) => isScannedForm(f) || isApiForm(f)),
    typeform: connectedOrDraftForms.some(isTypeform),
    jotform: connectedOrDraftForms.some(isJotform),
    total: facebookConnectedForms + connectedOrDraftForms.length,
  };

  const warehouseData = warehouse.data ?? null;
  const warehouseStatus =
    warehouseData == null
      ? null
      : { type: warehouseSyncType(warehouseData), isPaused: warehouseSyncIsPaused(warehouseData, twilioEnabled) };

  const sources: IntegrationsListSources = {
    backendIntegrations,
    audienceCredentials: audienceCredentials.data ?? [],
    adwordsAudiences: (audiences.data ?? []).filter((a) => a.type === 'adwords'),
    facebookAudiences: (audiences.data ?? []).filter((a) => a.type === 'facebook'),
    mparticleEnabled: mparticle.data != null,
    warehouseSync: warehouseStatus,
    // Null until all six per-database reads settle (no cards while loading).
    sqlImportsByDatabase: sqlImports.isPending ? null : sqlImports.byDatabase,
    reportingWebhooksCount: webhooks.data?.length ?? 0,
    forms,
    apiKeysCount: apiCredentials.data?.length ?? 0,
    segmentInPresent: segmentIn.data ?? false,
    segmentOutEnabled: segmentOut.data ?? false,
    cdpOutEnabled: cdpOut.data ?? false,
    enabledDataOutIds,
  };

  const config: IntegrationsListConfig = {
    isPublic: false,
    enabledActions,
    isTwilioWhiteLabeled: false,
    urlParamsEnabled: false,
    terms,
  };

  const isLoading =
    [
      environment,
      catalog,
      webhooks,
      audiences,
      audienceCredentials,
      warehouse,
      customForms,
      fbCredential,
      fbForms,
      mparticle,
      cdpOut,
      segmentIn,
      segmentOut,
      apiCredentials,
    ].some((q) => q.isLoading) || sqlImports.isPending;

  return { sources, config, isLoading };
}

/**
 * The directory tab's view model: the synthesized list restricted to the
 * directory's visible set, filtered by the current query-params, sorted, and
 * grouped (Direct / Developer / Webhook / Zapier).
 */
export function useDirectoryIntegrations(filters: IntegrationFilters) {
  const { sources, config, isLoading } = useIntegrationsListSources();
  const sqlImportAvailable = useFeatureFlag(AccountFeatureFlag.CdpJourneysSqlImportAvailable);

  return useMemo(() => {
    const all = buildIntegrationsList(sources, config);
    const visible = visibleIntegrationsDirectory(all);
    const filtered = directoryFilteredIntegrations(visible, filters, sqlImportAvailable);
    const sorted = sortIntegrations(filtered);
    const groups = groupIntegrations(sorted);
    return {
      isLoading,
      integrations: all,
      visibleIntegrations: visible,
      sortedIntegrations: sorted,
      groups,
      categoryOptions: computeCategoryOptions(visible),
    };
  }, [sources, config, filters, sqlImportAvailable, isLoading]);
}
